import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Users, UserPlus, Shield, UserCheck, Search, Phone, MapPin, Calendar, X, Maximize2, Minimize2, Eye, EyeOff } from 'lucide-react';
import axios from 'axios';
import { useNotifications } from '../../hooks/useNotifications';
import { useConfirm } from '../../contexts/ConfirmContext';
import { useAuth } from '../../contexts/AuthContext';
import { useEscapeKey } from '../../hooks/useEscapeKey';
import { useExpandable } from '../../hooks/useExpandable';
import Breadcrumbs from '../../components/Admin/Breadcrumbs';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { useTranslation } from 'react-i18next';
import { ActionButton, Button, IconButton, Pagination, RowActions } from '../../components/UI';
import { useCopyToClipboard } from '../../hooks/useCopyToClipboard';

interface User {
  _id: string;
  name: string;
  email: string;
  role: 'user' | 'admin' | 'super_admin';
  isActive: boolean;
  avatar?: string;
  phone?: string;
  country?: string;
  createdAt: string;
}

interface CreateUserModalProps {
  onClose: () => void;
  onCreateUser: (userData: { name: string; email: string; password: string; role: string }) => void;
}

const createUserSchema = yup.object({
  name: yup.string().required('Nome é obrigatório').min(2, 'Nome deve ter pelo menos 2 caracteres'),
  email: yup.string().required('Email é obrigatório').email('Email inválido'),
  password: yup.string().required('Senha é obrigatória').min(6, 'Senha deve ter pelo menos 6 caracteres'),
  confirmPassword: yup.string()
    .required('Confirmação de senha é obrigatória')
    .oneOf([yup.ref('password')], 'Senhas não coincidem'),
  role: yup.string().required('Tipo de usuário é obrigatório').oneOf(['user', 'admin', 'super_admin'], 'Tipo de usuário inválido'),
});

type CreateUserData = yup.InferType<typeof createUserSchema>;

const CreateUserModal: React.FC<CreateUserModalProps> = ({ onClose, onCreateUser }) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const { isExpanded, toggleExpanded } = useExpandable();
  useEscapeKey(onClose, true);

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset
  } = useForm<CreateUserData>({
    resolver: yupResolver(createUserSchema),
  });

  const onSubmit = async (data: CreateUserData) => {
    try {
      setIsSubmitting(true);
      await onCreateUser({
        name: data.name,
        email: data.email,
        password: data.password,
        role: data.role
      });
      reset();
    } catch (error) {
      console.error('Erro ao criar usuário:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-end sm:items-center justify-center z-50 p-0 sm:p-4">
      <div className={`bg-white rounded-t-2xl sm:rounded-lg shadow-xl w-full overflow-y-auto transition-all duration-200 ${
        isExpanded ? 'max-w-[95vw] max-h-[95vh]' : 'sm:max-w-md max-h-[90vh]'
      }`}>
        <div className="px-4 sm:px-6 py-4 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <h3 className="text-base sm:text-lg font-semibold text-gray-900">Criar Novo Usuário</h3>
            <div className="flex items-center gap-1">
              <button
                type="button"
                onClick={toggleExpanded}
                className="icon-btn rounded-lg text-gray-400 hover:bg-gray-100 hover:text-gray-600 transition-colors p-1.5"
                aria-label={isExpanded ? 'Restaurar' : 'Expandir'}
                title={isExpanded ? 'Restaurar' : 'Expandir'}
              >
                {isExpanded ? <Minimize2 className="w-5 h-5 sm:w-6 sm:h-6" /> : <Maximize2 className="w-5 h-5 sm:w-6 sm:h-6" />}
              </button>
              <IconButton action="close" onClick={onClose} iconClassName="w-5 h-5 sm:w-6 sm:h-6" />
            </div>
          </div>
        </div>

        <form onSubmit={handleSubmit(onSubmit)} className="px-4 sm:px-6 py-4">
          <div className="space-y-4">
            {/* Nome */}
            <div>
              <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">
                Nome Completo
              </label>
              <input
                {...register('name')}
                type="text"
                id="name"
                className="field"
                placeholder="Digite o nome completo"
              />
              {errors.name && (
                <p className="mt-1 text-sm text-red-600">{errors.name.message}</p>
              )}
            </div>

            {/* Email */}
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                Email
              </label>
              <input
                {...register('email')}
                type="email"
                id="email"
                className="field"
                placeholder="Digite o email"
              />
              {errors.email && (
                <p className="mt-1 text-sm text-red-600">{errors.email.message}</p>
              )}
            </div>

            {/* Senha */}
            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-1">
                Senha
              </label>
              <div className="relative">
                <input
                  {...register('password')}
                  type={showPassword ? 'text' : 'password'}
                  id="password"
                  className="field pr-10"
                  placeholder="Digite a senha"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute inset-y-0 right-0 pr-3 flex items-center"
                >
                  {showPassword ? <EyeOff className="w-5 h-5 text-gray-400" /> : <Eye className="w-5 h-5 text-gray-400" />}
                </button>
              </div>
              {errors.password && (
                <p className="mt-1 text-sm text-red-600">{errors.password.message}</p>
              )}
            </div>

            {/* Confirmar Senha */}
            <div>
              <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700 mb-1">
                Confirmar Senha
              </label>
              <div className="relative">
                <input
                  {...register('confirmPassword')}
                  type={showConfirmPassword ? 'text' : 'password'}
                  id="confirmPassword"
                  className="field pr-10"
                  placeholder="Confirme a senha"
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  className="absolute inset-y-0 right-0 pr-3 flex items-center"
                >
                  {showConfirmPassword ? <EyeOff className="w-5 h-5 text-gray-400" /> : <Eye className="w-5 h-5 text-gray-400" />}
                </button>
              </div>
              {errors.confirmPassword && (
                <p className="mt-1 text-sm text-red-600">{errors.confirmPassword.message}</p>
              )}
            </div>

            {/* Role */}
            <div>
              <label htmlFor="role" className="block text-sm font-medium text-gray-700 mb-1">
                Tipo de Usuário
              </label>
              <select
                {...register('role')}
                id="role"
                className="field field--select"
              >
                <option value="">Selecione o tipo de usuário</option>
                <option value="user">👤 Usuário</option>
                <option value="admin">👑 Administrador</option>
                <option value="super_admin">🔐 Super Admin</option>
              </select>
              {errors.role && (
                <p className="mt-1 text-sm text-red-600">{errors.role.message}</p>
              )}
            </div>
          </div>

          <div className="form-actions mt-6">
            <button
              type="button"
              onClick={onClose}
              className="w-full sm:w-auto px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
            >
              Cancelar
            </button>
            <Button size="sm" className="w-full sm:w-auto"
              type="submit"
              disabled={isSubmitting}>
              {isSubmitting ? 'Criando...' : 'Criar Usuário'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

interface InviteUserModalProps {
  onClose: () => void;
  onInvite: (invite: { name: string; email: string }) => Promise<{ inviteUrl?: string; emailSent?: boolean } | void>;
}

const InviteUserModal: React.FC<InviteUserModalProps> = ({ onClose, onInvite }) => {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [inviteUrl, setInviteUrl] = useState<string | null>(null);
  const [emailSent, setEmailSent] = useState<boolean | null>(null);
  const { isExpanded, toggleExpanded } = useExpandable();
  useEscapeKey(onClose, true);

  const isValid = name.trim().length >= 2 && /.+@.+\..+/.test(email);

  const { copy } = useCopyToClipboard();
  const copyLink = () => {
    if (inviteUrl) copy(inviteUrl);
  };

  const submit = async () => {
    if (!isValid) return;
    try {
      setIsSubmitting(true);
      setInviteUrl(null);
      setEmailSent(null);
      const res = await onInvite({ name: name.trim(), email: email.trim() });
      if (res?.inviteUrl) setInviteUrl(res.inviteUrl);
      if (res && 'emailSent' in res) setEmailSent(res.emailSent ?? true);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-end sm:items-center justify-center z-50 p-0 sm:p-4">
      <div className={`bg-white rounded-t-2xl sm:rounded-lg shadow-xl w-full max-h-[90vh] overflow-y-auto ${
        isExpanded ? 'max-w-[95vw] max-h-[95vh]' : 'sm:max-w-md'
      }`}>
        <div className="px-5 py-4 border-b border-gray-200 flex items-center justify-between">
          <h3 className="text-lg font-semibold text-gray-900">Enviar Convite</h3>
          <div className="flex items-center gap-1">
            <button onClick={toggleExpanded} className="icon-btn text-gray-400 hover:text-gray-600" title={isExpanded ? 'Restaurar' : 'Expandir'}>
              {isExpanded ? <Minimize2 className="w-5 h-5" /> : <Maximize2 className="w-5 h-5" />}
            </button>
            <IconButton action="close" onClick={onClose} />
          </div>
        </div>

        <div className="p-5 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Nome</label>
            <input
              value={name}
              onChange={(e) => setName(e.target.value)}
              type="text"
              className="field"
              placeholder="Nome do convidado"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
            <input
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              type="email"
              className="field"
              placeholder="email@exemplo.com"
            />
          </div>
          {inviteUrl && (
            <div className="space-y-2">
              {emailSent === false && (
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm">
                  <p className="text-amber-800 font-medium">E-mail não foi enviado</p>
                  <p className="text-amber-700 mt-1">Configure no servidor SENDGRID_API_KEY, MAILERSEND_API_KEY ou SMTP (SMTP_HOST + SMTP_USER/SMTP_PASSWORD) para enviar convites por e-mail. Envie o link abaixo manualmente ao convidado.</p>
                </div>
              )}
              <div className="bg-green-50 border border-green-200 rounded-lg p-3 text-sm">
                <p className="text-green-800 font-medium">Convite gerado com sucesso</p>
                <p className="mt-1 break-all">
                  <a className="text-green-700 underline" href={inviteUrl} target="_blank" rel="noreferrer">{inviteUrl}</a>
                </p>
                <Button size="sm" className="mt-2" type="button" onClick={copyLink}>Copiar link</Button>
              </div>
            </div>
          )}
        </div>

        <div className="form-actions px-5 py-4 border-t border-gray-200">
          <button onClick={onClose} className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200">Fechar</button>
          <Button size="sm"
            onClick={submit}
            disabled={!isValid || isSubmitting}>
            {isSubmitting ? 'Enviando...' : 'Enviar Convite'}
          </Button>
        </div>
      </div>
    </div>
  );
};

const AdminUsers: React.FC = () => {
  const { t } = useTranslation();
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [roleFilter, setRoleFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const { isExpanded: isUserDetailExpanded, setIsExpanded: setIsUserDetailExpanded, toggleExpanded: toggleUserDetailExpanded } = useExpandable();
  useEscapeKey(() => { setSelectedUser(null); setIsUserDetailExpanded(false); }, !!selectedUser);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showInviteModal, setShowInviteModal] = useState(false);
  const notifications = useNotifications();
  const confirm = useConfirm();
  const { user } = useAuth();
  const isProduction = process.env.NODE_ENV === 'production';

  const roleOptions = [
    { value: '', label: 'Todos os cargos' },
    { value: 'user', label: 'Usuário' },
    { value: 'admin', label: 'Administrador' },
    { value: 'super_admin', label: 'Super Administrador' },
  ];

  const statusOptions = [
    { value: '', label: 'Todos os status' },
    { value: 'active', label: 'Ativo' },
    { value: 'inactive', label: 'Inativo' },
  ];

  const fetchUsers = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({
        page: currentPage.toString(),
        limit: '10',
        ...(searchTerm && { search: searchTerm }),
        ...(roleFilter && { role: roleFilter }),
        ...(statusFilter && { status: statusFilter }),
      });

      const response = await axios.get(`/users?${params}`);
      setUsers(response.data.users);
      setTotalPages(response.data.totalPages);
    } catch (error) {
      console.error('Erro ao carregar usuários:', error);
    } finally {
      setLoading(false);
    }
  }, [searchTerm, roleFilter, statusFilter, currentPage]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  const handleRoleChange = async (userId: string, newRole: string) => {
    try {
      await axios.put(`/users/${userId}`, {
        role: newRole
      });
      
      setUsers(users.map(user => 
        user._id === userId 
          ? { ...user, role: newRole as any }
          : user
      ));
      
      notifications.success.userUpdated();
    } catch (error: any) {
      console.error('Erro ao alterar cargo:', error);
      notifications.error.userUpdate(error.response?.data?.message || 'Erro ao alterar cargo');
    }
  };

  const handleStatusChange = async (userId: string, isActive: boolean) => {
    try {
      await axios.put(`/users/${userId}`, {
        isActive: !isActive
      });
      
      setUsers(users.map(user => 
        user._id === userId 
          ? { ...user, isActive: !isActive }
          : user
      ));
      
      notifications.success.userUpdated();
    } catch (error: any) {
      console.error('Erro ao alterar status:', error);
      notifications.error.userUpdate(error.response?.data?.message || 'Erro ao alterar status');
    }
  };

  const handleDeleteUser = async (userId: string) => {
    if (!(await confirm({ message: t('admin.userDeleteConfirm'), confirmText: t('common.delete'), variant: 'danger' }))) return;
    try {
      await axios.delete(`/users/${userId}`);
      setUsers(users.filter(user => user._id !== userId));
      notifications.success.userDeleted();
    } catch (error: any) {
      console.error('Erro ao deletar usuário:', error);
      notifications.error.userDelete(error.response?.data?.message || 'Erro ao deletar usuário');
    }
  };

  const handleCreateUser = async (userData: { name: string; email: string; password: string; role: string }) => {
    try {
      const response = await axios.post('/auth/register', userData);
      const newUser = response.data.user;
      
      setUsers([newUser, ...users]);
      setShowCreateModal(false);
      notifications.success.userCreated();
    } catch (error: any) {
      console.error('Erro ao criar usuário:', error);
      notifications.error.userCreate(error.response?.data?.message || 'Erro ao criar usuário');
    }
  };

  const getRoleColor = (role: string) => {
    const colors = {
      user: 'bg-gray-100 text-gray-800',
      admin: 'bg-blue-100 text-blue-800',
      super_admin: 'bg-purple-100 text-purple-800',
    };
    return colors[role as keyof typeof colors] || colors.user;
  };

  const getRoleIcon = (role: string) => {
    switch (role) {
      case 'super_admin':
        return <Shield className="w-4 h-4" />;
      case 'admin':
        return <UserCheck className="w-4 h-4" />;
      default:
        return <Users className="w-4 h-4" />;
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('pt-BR', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow">
        <div className="max-w-7xl mx-auto py-6 px-4 sm:px-6 lg:px-8">
          <div className="mb-4">
            <Breadcrumbs />
          </div>
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">{t('admin.users')}</h1>
              <p className="text-gray-600">Administre usuários e permissões do sistema</p>
            </div>
            <div className="flex flex-wrap items-center gap-2 sm:gap-4">
              <span className="text-sm text-gray-500">
                {users.filter(u => u.isActive).length} ativos
              </span>
              <Button variant="ghost" size="sm"
                to="/admin/invites">
                Ver Convites
              </Button>
              {/* Produção: enviar convite; Dev/Staging: criar usuário */}
              {user?.role === 'super_admin' && (
                isProduction ? (
                  <Button size="sm"
                    onClick={() => setShowInviteModal(true)}>
                    <UserPlus className="w-4 h-4 mr-2" />
                    Enviar Convite
                  </Button>
                ) : (
                  <Button size="sm"
                    onClick={() => setShowCreateModal(true)}>
                    <UserPlus className="w-4 h-4 mr-2" />
                    Criar Usuário
                  </Button>
                )
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto py-6 px-4 sm:px-6 lg:px-8">
        {/* Filters */}
        <div className="bg-white rounded-lg shadow mb-6 p-6">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            {/* Search */}
            <div className="md:col-span-2">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                <input
                  type="text"
                  placeholder="Buscar usuários..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="field pl-10 pr-4"
                />
              </div>
            </div>

            {/* Role Filter */}
            <div>
              <select
                value={roleFilter}
                onChange={(e) => setRoleFilter(e.target.value)}
                className="field field--select"
              >
                {roleOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>

            {/* Status Filter */}
            <div>
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="field field--select"
              >
                {statusOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {/* Users Table */}
        <div className="bg-white rounded-lg shadow overflow-hidden">
          {loading ? (
            <div className="p-8 text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto"></div>
              <p className="mt-2 text-gray-600">Carregando usuários...</p>
            </div>
          ) : users.length === 0 ? (
            <div className="p-8 text-center">
              <div className="text-gray-400 mb-4">
                <Users className="w-16 h-16 mx-auto" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Nenhum usuário encontrado</h3>
              <p className="text-gray-600">
                {searchTerm || roleFilter || statusFilter 
                  ? 'Tente ajustar seus filtros de busca.'
                  : 'Ainda não há usuários cadastrados.'
                }
              </p>
            </div>
          ) : (
            <>
              {/* Desktop Table */}
              <div className="hidden md:block overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Usuário
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Cargo
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      País
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Data de Cadastro
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Ações
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {users.map((user) => (
                    <tr key={user._id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className="flex-shrink-0 h-10 w-10">
                            {user.avatar ? (
                              <img
                                className="h-10 w-10 rounded-full object-cover"
                                src={user.avatar}
                                alt={user.name}
                              />
                            ) : (
                              <div className="h-10 w-10 bg-primary-100 rounded-full flex items-center justify-center">
                                <span className="text-primary-600 font-medium text-sm">
                                  {user.name.charAt(0).toUpperCase()}
                                </span>
                              </div>
                            )}
                          </div>
                          <div className="ml-4">
                            <div className="text-sm font-medium text-gray-900">
                              {user.name}
                            </div>
                            <div className="text-sm text-gray-500">
                              {user.email}
                            </div>
                            {user.phone && (
                              <div className="text-sm text-gray-500">
                                {user.phone}
                              </div>
                            )}
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center space-x-2">
                          <span className={`px-2 py-1 text-xs font-medium rounded-full flex items-center space-x-1 ${getRoleColor(user.role)}`}>
                            {getRoleIcon(user.role)}
                            <span>{user.role}</span>
                          </span>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                          user.isActive 
                            ? 'bg-green-100 text-green-800' 
                            : 'bg-red-100 text-red-800'
                        }`}>
                          {user.isActive ? 'Ativo' : 'Inativo'}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {user.country || '-'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {formatDate(user.createdAt)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <RowActions align="end">
                          <ActionButton action="view" label="Ver detalhes" onClick={() => setSelectedUser(user)} />
                          <ActionButton
                            action={user.isActive ? 'deactivate' : 'activate'}
                            onClick={() => handleStatusChange(user._id, user.isActive)}
                          />
                          <ActionButton action="delete" onClick={() => handleDeleteUser(user._id)} />
                        </RowActions>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Mobile Cards */}
            <div className="md:hidden space-y-4">
              {users.map((user) => (
                <div key={user._id} className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-center space-x-3">
                      <div className="flex-shrink-0 h-10 w-10">
                        {user.avatar ? (
                          <img
                            className="h-10 w-10 rounded-full object-cover"
                            src={user.avatar}
                            alt={user.name}
                          />
                        ) : (
                          <div className="h-10 w-10 rounded-full bg-gray-300 flex items-center justify-center">
                            <Users className="h-5 w-5 text-gray-600" />
                          </div>
                        )}
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-medium text-gray-900 truncate">{user.name}</p>
                        <p className="text-sm text-gray-500 truncate">{user.email}</p>
                      </div>
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-xs">
                    <div>
                      <span className="text-gray-500">Cargo:</span>
                      <p className="font-medium text-gray-900">
                        {user.role === 'super_admin' && '🔐 Super Admin'}
                        {user.role === 'admin' && '👑 Administrador'}
                        {user.role === 'user' && '👤 Usuário'}
                      </p>
                    </div>
                    <div>
                      <span className="text-gray-500">Status:</span>
                      <p className={`font-medium ${user.isActive ? 'text-green-600' : 'text-red-600'}`}>
                        {user.isActive ? '✅ Ativo' : '❌ Inativo'}
                      </p>
                    </div>
                    {user.country && (
                      <div>
                        <span className="text-gray-500">País:</span>
                        <p className="font-medium text-gray-900">{user.country}</p>
                      </div>
                    )}
                    <div>
                      <span className="text-gray-500">Criado:</span>
                      <p className="font-medium text-gray-900">
                        {new Date(user.createdAt).toLocaleDateString('pt-BR')}
                      </p>
                    </div>
                  </div>
                  <RowActions className="mt-3">
                    <ActionButton action="view" label="Ver detalhes" onClick={() => setSelectedUser(user)} />
                    <ActionButton
                      action={user.isActive ? 'deactivate' : 'activate'}
                      onClick={() => handleStatusChange(user._id, user.isActive)}
                    />
                    <ActionButton action="delete" onClick={() => handleDeleteUser(user._id)} />
                  </RowActions>
                </div>
              ))}
            </div>
            </>
          )}

          <Pagination currentPage={currentPage} totalPages={totalPages} onChange={setCurrentPage} />
        </div>
      </div>

      {/* User Detail Modal */}
      {selectedUser && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-end sm:items-center justify-center z-50 p-0 sm:p-4">
          <div className={`relative bg-white border shadow-lg rounded-t-2xl sm:rounded-md p-5 w-full overflow-y-auto transition-all duration-200 ${
            isUserDetailExpanded ? 'max-w-[95vw] max-h-[95vh]' : 'sm:max-w-2xl max-h-[85vh]'
          }`}>
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-bold text-gray-900">Detalhes do Usuário</h3>
              <div className="flex items-center gap-1">
                <button
                  onClick={toggleUserDetailExpanded}
                  className="icon-btn text-gray-400 hover:text-gray-600 p-1"
                  title={isUserDetailExpanded ? 'Restaurar' : 'Expandir'}
                >
                  {isUserDetailExpanded ? <Minimize2 className="w-5 h-5" /> : <Maximize2 className="w-5 h-5" />}
                </button>
                <button
                  onClick={() => { setSelectedUser(null); setIsUserDetailExpanded(false); }}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <span className="sr-only">Fechar</span>
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>
            
            <div className="space-y-4">
              <div className="flex items-center space-x-4">
                {selectedUser.avatar ? (
                  <img
                    className="h-16 w-16 rounded-full object-cover"
                    src={selectedUser.avatar}
                    alt={selectedUser.name}
                  />
                ) : (
                  <div className="h-16 w-16 bg-primary-100 rounded-full flex items-center justify-center">
                    <span className="text-primary-600 font-medium text-xl">
                      {selectedUser.name.charAt(0).toUpperCase()}
                    </span>
                  </div>
                )}
                <div>
                  <h4 className="text-xl font-semibold text-gray-900">{selectedUser.name}</h4>
                  <p className="text-gray-600">{selectedUser.email}</p>
                </div>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <h5 className="font-semibold text-gray-900 mb-2">Informações Pessoais</h5>
                  <div className="space-y-2">
                    {selectedUser.phone && (
                      <div className="flex items-center space-x-2">
                        <Phone className="w-4 h-4 text-gray-400" />
                        <span className="text-sm text-gray-600">{selectedUser.phone}</span>
                      </div>
                    )}
                    {selectedUser.country && (
                      <div className="flex items-center space-x-2">
                        <MapPin className="w-4 h-4 text-gray-400" />
                        <span className="text-sm text-gray-600">{selectedUser.country}</span>
                      </div>
                    )}
                    <div className="flex items-center space-x-2">
                      <Calendar className="w-4 h-4 text-gray-400" />
                      <span className="text-sm text-gray-600">
                        Cadastrado em {formatDate(selectedUser.createdAt)}
                      </span>
                    </div>
                  </div>
                </div>
                
                <div>
                  <h5 className="font-semibold text-gray-900 mb-2">Permissões</h5>
                  <div className="space-y-2">
                    <div className="flex items-center space-x-2">
                      <span className={`px-2 py-1 text-xs font-medium rounded-full ${getRoleColor(selectedUser.role)}`}>
                        {selectedUser.role}
                      </span>
                      {/* Apenas super_admin pode alterar roles */}
                      {user?.role === 'super_admin' && (
                        <select
                          value={selectedUser.role}
                          onChange={(e) => handleRoleChange(selectedUser._id, e.target.value)}
                          className="field field--select ml-2"
                        >
                          <option value="user">👤 Usuário</option>
                          <option value="admin">👑 Administrador</option>
                          <option value="super_admin">🔐 Super Admin</option>
                        </select>
                      )}
                    </div>
                    <div className="flex items-center space-x-2">
                      <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                        selectedUser.isActive 
                          ? 'bg-green-100 text-green-800' 
                          : 'bg-red-100 text-red-800'
                      }`}>
                        {selectedUser.isActive ? 'Ativo' : 'Inativo'}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="form-actions mt-6">
              <Button variant="ghost" size="sm"
                onClick={() => { setSelectedUser(null); setIsUserDetailExpanded(false); }}>
                Fechar
              </Button>
              <Button
                size="sm"
                variant={selectedUser.isActive ? 'danger' : 'primary'}
                icon={selectedUser.isActive ? 'deactivate' : 'activate'}
                onClick={() => {
                  handleStatusChange(selectedUser._id, selectedUser.isActive);
                  setSelectedUser(null);
                  setIsUserDetailExpanded(false);
                }}
              >
                {selectedUser.isActive ? 'Desativar' : 'Ativar'}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Criação de Usuário */}
      {showCreateModal && (
        <CreateUserModal
          onClose={() => setShowCreateModal(false)}
          onCreateUser={handleCreateUser}
        />
      )}

      {/* Modal de Convite */}
      {showInviteModal && (
        <InviteUserModal
          onClose={() => setShowInviteModal(false)}
          onInvite={async ({ email, name }) => {
            try {
              const res = await axios.post('/auth/invite', { email, name });
              notifications.showSuccess('Sucesso', res.data?.emailSent ? 'Convite enviado por e-mail ao convidado.' : 'Convite gerado. Copie o link e envie manualmente (configure SendGrid, MailerSend ou SMTP no servidor).');
              return { inviteUrl: res.data?.inviteUrl, emailSent: res.data?.emailSent };
            } catch (err: any) {
              notifications.showError('Erro', err.response?.data?.message || 'Erro ao enviar convite');
            }
          }}
        />
      )}
    </div>
  );
};

export default AdminUsers;
